import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';

export const CookiePolicyPage: React.FC = () => {
  return (
    <div className="canvas-dark" style={{ minHeight: '100vh', paddingTop: '130px', paddingBottom: '80px', backgroundColor: '#121212' }}>
      
      {/* Hero */}
      <header className="container" style={{ padding: '0 20px 48px', maxWidth: '880px' }}>
        <div className="eyebrow" style={{ marginBottom: '12px' }}>Cookie &amp; Storage Specification</div>
        <h1 
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: '-1px',
            color: '#FFFFFF',
            margin: '0 0 16px'
          }}
        >
          Cookie Policy
        </h1>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
          Effective Date: January 1, 2026 &bull; Last Updated: September 2026. <br />
          Draway Auth does not deploy third-party advertising cookies, user tracking pixels, or cross-site tracking scripts.
        </p>
      </header>

      {/* Content Layout */}
      <div className="container" style={{ maxWidth: '880px', padding: '0 20px' }}>
        <div 
          style={{ 
            backgroundColor: '#1A1A1A', 
            border: '1px solid rgba(255, 255, 255, 0.08)', 
            borderRadius: '8px', 
            padding: '40px 36px', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '36px' 
          }}
        >
          {/* Quick Summary Strip */}
          <div 
            style={{ 
              backgroundColor: '#121212', 
              border: '1px solid #374151', 
              borderRadius: '8px', 
              padding: '22px 24px' 
            }}
          >
            <h4 style={{ color: '#FFFFFF', fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <CheckCircle2 size={18} color="#6D7CFF" /> Zero Ad-Tracking Guarantee
            </h4>
            <p style={{ color: '#D7D7D7', fontSize: '14px', marginTop: '10px', lineHeight: 1.6 }}>
              We only issue <strong style={{ color: '#FFFFFF' }}>Strictly Necessary Cookies</strong> required for secure session persistence, CSRF protection, and fraud defense. You will never find Google AdSense, Meta Pixel, or data-broker trackers on Draway Auth.
            </p>
          </div>

          {/* Section 1 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>1. What Cookies We Store</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              The following cookies are issued by Draway Auth API endpoints and client SDKs:
            </p>
            <div style={{ marginTop: '16px', overflowX: 'auto', border: '1px solid #374151', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#D7D7D7' }}>
                <thead>
                  <tr style={{ background: '#151515', borderBottom: '1px solid #374151', textAlign: 'left' }}>
                    <th style={{ padding: '12px 16px', color: '#FFFFFF', fontWeight: 500 }}>Cookie Name</th>
                    <th style={{ padding: '12px 16px', color: '#FFFFFF', fontWeight: 500 }}>Type</th>
                    <th style={{ padding: '12px 16px', color: '#FFFFFF', fontWeight: 500 }}>Security Attributes</th>
                    <th style={{ padding: '12px 16px', color: '#FFFFFF', fontWeight: 500 }}>Lifespan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '12px 16px', color: '#6D7CFF', fontFamily: "'JetBrains Mono', monospace" }}>draway_at</td>
                    <td style={{ padding: '12px 16px' }}>Access Token Session</td>
                    <td style={{ padding: '12px 16px' }}><code>HttpOnly; Secure; SameSite=Lax</code></td>
                    <td style={{ padding: '12px 16px' }}>15 minutes</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '12px 16px', color: '#6D7CFF', fontFamily: "'JetBrains Mono', monospace" }}>draway_rt</td>
                    <td style={{ padding: '12px 16px' }}>Refresh Token (Rotated)</td>
                    <td style={{ padding: '12px 16px' }}><code>HttpOnly; Secure; SameSite=Strict; Path=/v1/auth/refresh</code></td>
                    <td style={{ padding: '12px 16px' }}>7 - 30 days</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '12px 16px', color: '#6D7CFF', fontFamily: "'JetBrains Mono', monospace" }}>draway_csrf</td>
                    <td style={{ padding: '12px 16px' }}>CSRF Double-Submit Token</td>
                    <td style={{ padding: '12px 16px' }}><code>Secure; SameSite=Lax</code></td>
                    <td style={{ padding: '12px 16px' }}>Session</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px 16px', color: '#6D7CFF', fontFamily: "'JetBrains Mono', monospace" }}>cf_clearance</td>
                    <td style={{ padding: '12px 16px' }}>Cloudflare DDoS Defense</td>
                    <td style={{ padding: '12px 16px' }}><code>HttpOnly; Secure; SameSite=None</code></td>
                    <td style={{ padding: '12px 16px' }}>Up to 1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>2. Why HttpOnly is Mandatory</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              Unlike older identity providers that encourage saving raw JWT tokens inside <code>localStorage</code> (where any malicious third-party script or XSS vulnerability can extract them instantly), Draway Auth utilizes <code>HttpOnly</code> cookies. This ensures that browser JavaScript cannot read your session credentials under any circumstances.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>3. Controlling Cookie Preferences</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              Because all cookies utilized on Draway Auth are classified as <strong style={{ color: '#FFFFFF' }}>Strictly Necessary</strong> for authentication and platform security under ePrivacy Directive (Directive 2002/58/EC) Article 5(3), prior consent banners are not legally required. However, you can manage or clear cookies at any time directly through your browser settings. Note that disabling essential cookies will prevent authenticated console access.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>4. Questions Regarding Cookies</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              For questions regarding our cookie practices, reach out to our privacy compliance team at <a href="mailto:privacy@draway.in" style={{ color: '#6D7CFF', textDecoration: 'underline' }}>privacy@draway.in</a> or review our full <Link to="/privacy" style={{ color: '#6D7CFF', textDecoration: 'underline' }}>Privacy Policy</Link>.
            </p>
          </section>

        </div>
      </div>

    </div>
  );
};

export default CookiePolicyPage;
